import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AxiosError } from "axios";
import qs from "qs";
import Button from "@aleaplay/web-components/ui/Button";
import Input from "@aleaplay/web-components/ui/Input";
import Modal from "@aleaplay/web-components/ui/Modal";

import { getGameLink } from "@/v1/services/launch";
import { ErrorResponse } from "@/v1/types/global";
import isValidURL from "@/v1/utils/isValidURL";

interface GameLinkModalProps {
  visible: boolean;
  onClose: () => void;
}

const GameLinkModal: React.FC<GameLinkModalProps> = ({
  visible = false,
  onClose = () => undefined,
}) => {
  const intl = useIntl();
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>({ visible: false, type: "empty" });
  const [gameLink, setGameLink] = useState<any>(null);

  useEffect(() => {
    return () => {
      setLoading(false);
      setError({ visible: false, type: "empty" });
      setGameLink(null);
      setValue("");
    };
  }, [visible]);

  const getErrorMessage = () => {
    if (error.type === "empty") {
      return "get-embedded-game-link.error";
    } else if (error.type === "not-found") {
      return "get-embedded-game-link.error-not-found";
    }
    return "label.generic-error";
  };

  const copyLink = async ({ url }: { url: string }) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(url);
    }
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (!value) {
      setError({
        visible: true,
        type: "empty",
      });
      return;
    }
    setLoading(true);
    try {
      let target;
      const url = isValidURL(value);
      if (url) {
        // eslint-disable-next-line prefer-destructuring
        target = qs.parse(url.search, {
          ignoreQueryPrefix: true,
        }).target;
      } else {
        target = value;
      }

      const res = await getGameLink(target as string);
      setGameLink(res);
      copyLink(res);
    } catch (err) {
      const errorResponse = (err &&
        typeof err === "object" &&
        (err as AxiosError<ErrorResponse>)?.response?.data) as ErrorResponse;
      setError({
        visible: true,
        type: errorResponse.status === 404 ? "not-found" : "network",
      });
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value !== value) {
      setValue(e.target.value);
      setError(false);
    }
  };

  return (
    <Modal
      visible={visible}
      title={intl.formatMessage({ id: "label.get-emmbedded-game-link" })}
      actions={[
        <Button key="cancel" modifier="secondary" onClick={onClose}>
          <FormattedMessage id="label.cancel" />
        </Button>,
        <Button key="confirm" modifier="primary" type="submit" onClick={onSubmit}>
          <FormattedMessage id="label.get-link" />
        </Button>,
      ]}
      footerMessage={
        <FormattedMessage
          id="label.marked-fields-mandatory"
          values={{ marked: <span className="highlight">*</span> }}
        />
      }
      loading={loading}
      domNode={document.querySelector("#root")}
      onClose={onClose}
    >
      <FormattedMessage id="get-emmbedded-game-link.label" />
      <span>:</span>
      <br />
      <br />
      <form onSubmit={() => onSubmit}>
        <Input
          error={error.visible}
          errorMessage={intl.formatMessage({
            id: getErrorMessage(),
          })}
          label={intl.formatMessage({ id: "label.display-url" })}
          placeholder={intl.formatMessage({
            id: "get-embedded-game-link.placeholder",
          })}
          required
          value={value}
          onChange={onChange}
        />
      </form>
      {gameLink && (
        <>
          <FormattedMessage id="label.game-link-has-been-copied-to-clipboard" tagName="p" />
          {gameLink.url}
        </>
      )}
    </Modal>
  );
};

export default GameLinkModal;
